import { AspectRatio, Box, Flex, Heading, Icon, Image, Text } from "@highoutput/ui-components";
import MarkdownIt from "markdown-it";
import Link from "next/link";
import type { PostCoreDataFragment } from "~/__generated__/graphql";
import takeFirstParagraph from "~/utils/take-first-paragraph";
import ArrowUpRightIcon from "../icons/arrow-up-right";

type PostProps = {
  data: PostCoreDataFragment;
};

const md = new MarkdownIt({
  html: true,
});

export default function Post({ data }: PostProps) {
  return (
    <Box pr={{ base: "16px", lg: "unset" }} mx="auto">
      <AspectRatio
        position="relative"
        width="full"
        height={{
          base: "240px",
          md: "350px" /* added to cover md breakpoint 🚀 */,
          lg: "276px",
          xl: "276px",
          "2xl": "276px",
        }}
      >
        {!!data.attributes?.cover.data?.attributes?.url && (
          <Image
            src={data.attributes?.cover.data?.attributes?.url as string}
            alt="Cover"
            width={600}
            height={400}
            data-testid={`posts.list.${data.id}.cover`}
          />
        )}
      </AspectRatio>

      <Text
        color="secondary.default"
        variant="14/14"
        letterSpacing="0.02em"
        marginTop="32px"
        data-testid={`posts.list.${data.id}.author`}
      >
        {`by ${data.attributes?.author?.data?.attributes?.name}` ?? "unknown"}
      </Text>

      <Flex
        as={Link}
        position="relative"
        zIndex={1}
        href={`/blog/${data.attributes?.slug}`}
        marginTop="12px"
        alignItems="start"
        justifyContent="space-between"
        gap="4"
        _hover={{
          "& > h2": {
            textDecoration: "underline",
          },
        }}
        data-testid={`posts.list.${data.id}.view-btn`}
      >
        <Heading
          variant={{
            base: "24/28",
            lg: "32/36",
          }}
          letterSpacing={{
            base: "-0.02em",
            lg: "-0.015em",
          }}
          overflow="hidden"
          maxWidth="600px"
          title={data.attributes?.title}
          data-testid={`posts.list.${data.id}.title`}
          fontWeight={400}
        >
          {data.attributes?.title}
        </Heading>

        <Box>
          <Icon as={ArrowUpRightIcon} width="24px" height="24px" />
        </Box>
      </Flex>

      {data.attributes?.content && (
        <Text
          marginTop="12px"
          variant={{
            base: "16/24",
            lg: "18/28",
          }}
          letterSpacing={{
            base: "0.02em",
            lg: "unset",
          }}
          color="rgba(13,48,80,0.8)"
          noOfLines={4}
          overflow="hidden"
          data-testid={`posts.list.${data.id}.description`}
          dangerouslySetInnerHTML={{
            __html: md.render(takeFirstParagraph(data.attributes.content)),
          }}
        />
      )}
    </Box>
  );
}
